import { handleize } from '../helpers';
import sendQuery from './send-query';
import productEdges from './product-edges';

const findBackVariantImage = (gql_product, variant_id) => {
  // Find the index of the variant with the given id
  const variant_index = gql_product.variants.edges.findIndex(edge => edge.node.id === variant_id);
  const variant = gql_product.variants.edges[variant_index].node;
  const color_value = (variant.selectedOptions.find(opt => opt.name === 'Color') || {}).value || '';
  const color_handle = handleize(color_value);
  let variant_back_image = '';

  // Find the next variant with a different color
  let found_same_color = false;
  for (let i = 0; i < gql_product.variants.edges.length; i++) {
    const find_variant = gql_product.variants.edges[i].node;
    const find_color_value = (find_variant.selectedOptions.find(opt => opt.name === 'Color') || {}).value || '';
    const find_color_handle = handleize(find_color_value);

    if (find_color_handle === color_handle) {
      found_same_color = true;
      continue;
    } else if (found_same_color && variant_back_image === '') {
      const next_variant_with_different_color = gql_product.variants.edges[i].node;
      const next_front_image = next_variant_with_different_color.image.url;
      const next_front_image_index = gql_product.images.edges.findIndex(edge => edge.node.url === next_front_image);

      if (typeof gql_product.images.edges[next_front_image_index - 1] !== 'undefined') {
        variant_back_image = gql_product.images.edges[next_front_image_index - 1].node.url;
      }
      
      found_same_color = false;
      break;
    }
  }

  // Last color variant
  if (found_same_color && variant_back_image === '') {
    variant_back_image = gql_product.images.edges[gql_product.images.edges.length - 1].node.url;
  }

  return variant_back_image;
};

const swatchesHtml = (gql_product) => {
  let swatches = '';

  // Only 1 per color
  const colors = [];
  gql_product.variants.edges.forEach(gql_variant => {
    const color_option = gql_variant.node.selectedOptions.find(opt => opt.name === 'Color');
    const color_value = color_option ? color_option.value : '';
    const color_handle = handleize(color_value);

    if (!colors.includes(color_handle)) {
      colors.push(color_handle);

      // Extract back image
      let front_image_url = '';
      let back_image_url = '';
      if (typeof gql_variant.node.image !== 'undefined' && gql_variant.node.image !== null) {
        front_image_url = gql_variant.node.image.url;
        back_image_url = findBackVariantImage(gql_product, gql_variant.node.id);
      }

      swatches += `<div data-color-swatch class="color-swatch" role="img" tabindex="0" data-variant-id="${gql_variant.node.id.split('/').pop()}" data-color-value="${color_value}" data-color-handle="${color_handle}" data-front="${front_image_url}" data-back="${back_image_url}"><span></span></div>`;
    }
  });

  return swatches;
};

const prettyfyProductsByID = (gql_products) => {
  const products = [];

  gql_products.forEach(gql_product => {
    const formatted_variants = [];
    gql_product.node.variants.edges.forEach(gql_variant => {
      // console.log('variant id', Number(gql_variant.node.id.split('/').pop()));

      const variant_color = gql_variant.node.selectedOptions.find(opt => opt.name === 'Color');
      const variant_size = gql_variant.node.selectedOptions.find(opt => opt.name === 'Size');

      if (typeof variant_color !== 'undefined' && typeof variant_size !== 'undefined') {
        if (typeof gql_variant.node.image === 'undefined' || gql_variant.node.image === null) {
          console.log('Variant has missing image: ', gql_variant);
        }
        formatted_variants.push({
          id: Number(gql_variant.node.id.split('/').pop()),
          title: gql_variant.node.title || '',
          image: (typeof gql_variant.node.image === 'undefined' || gql_variant.node.image === null) ? '' : gql_variant.node.image.url || '',
          image_alt: (typeof gql_variant.node.image === 'undefined' || gql_variant.node.image === null) ? '' : gql_variant.node.image.altText || '',
          price: String(parseFloat(gql_variant.node.price.amount) * 100),
          compare_at_price: (gql_variant.node.compareAtPrice === null) ? '0' : String(parseFloat(gql_variant.node.compareAtPrice.amount) * 100) || '0',
          available: gql_variant.node.availableForSale,
          inventory_quantity: gql_variant.node.quantityAvailable,
          color: variant_color.value || '',
          size: variant_size.value || '', 
          online_inventory: (typeof gql_variant.node.astrOnlineInventory === 'undefined' || gql_variant.node.astrOnlineInventory === null) ? null : Number(gql_variant.node.astrOnlineInventory.value) || null,
          online_inventory_style: (typeof gql_variant.node.astrOnlineInventoryStyle === 'undefined' || gql_variant.node.astrOnlineInventoryStyle === null) ? null : Number(gql_variant.node.astrOnlineInventoryStyle.value) || null,
          style_flag_standard: (typeof gql_variant.node.astrStyleFlagStandard === 'undefined' || gql_variant.node.astrStyleFlagStandard === null) ? null : gql_variant.node.astrStyleFlagStandard.value || null,
          style_flag_plus: (typeof gql_variant.node.astrStyleFlagPlus === 'undefined' || gql_variant.node.astrStyleFlagPlus === null) ? null : gql_variant.node.astrStyleFlagPlus.value || null,
        });
      } else {
        console.log('Cannot find color or size for variant', gql_variant.node);
      }
    });

    const store_domain = window.Shopify.shop.includes('staging') ? `https://${window.Shopify.shop}` : 'https://www.astrthelabel.com';

    if (formatted_variants.length > 0) {
      const product_obj = {
        id: Number(gql_product.node.id.split('/').pop()),
        handle: gql_product.node.handle,
        available: gql_product.node.availableForSale,
        title: gql_product.node.title || '',
        url: (gql_product.node.onlineStoreUrl === null) ? `/products/${gql_product.node.handle}` : gql_product.node.onlineStoreUrl.replace(store_domain, '') || '', // password protected store will return null for this field
        type: gql_product.node.productType || '',
        from: (gql_product.node.priceRange.maxVariantPrice.amount !== gql_product.node.priceRange.minVariantPrice.amount) ? String(parseFloat(gql_product.node.priceRange.minVariantPrice.amount) * 100) : '',
        compare_at_price: (gql_product.node.compareAtPriceRange === null) ? '0' : (gql_product.node.compareAtPriceRange.maxVariantPrice !== null && gql_product.node.compareAtPriceRange.maxVariantPrice.amount !== '0.0') ? String(parseFloat(gql_product.node.compareAtPriceRange.maxVariantPrice.amount) * 100) : '0',
        swatches: swatchesHtml(gql_product.node),
        swatches_raw: (typeof gql_product.node.astrSwatches === 'undefined' || gql_product.node.astrSwatches === null) ? null : gql_product.node.astrSwatches.value || null,
        variants: formatted_variants,
        images: gql_product.node.images.edges.map(item => item.node.url) || [],
        images_raw: (typeof gql_product.node.astrImagesDetails === 'undefined' || gql_product.node.astrImagesDetails === null) ? null : gql_product.node.astrImagesDetails.value || null,
        okendo_summary_data: (typeof gql_product.node.okendoSummaryData === 'undefined' || gql_product.node.okendoSummaryData === null) ? null : gql_product.node.okendoSummaryData.value || null,
        bestseller_14: (typeof gql_product.node.astrBestseller14 === 'undefined' || gql_product.node.astrBestseller14 === null) ? null : Number(gql_product.node.astrBestseller14.value) || null,
        bestseller_30: (typeof gql_product.node.astrBestseller30 === 'undefined' || gql_product.node.astrBestseller30 === null) ? null : Number(gql_product.node.astrBestseller30.value) || null,
      };

      if (typeof gql_product.node.tags !== 'undefined' && gql_product.node.tags !== null) {
        product_obj.tags = gql_product.node.tags;
      }

      if (typeof gql_product.node.collections !== 'undefined' && gql_product.node.collections !== null) {
        product_obj.collections = (gql_product.node.collections.edges).map(item => item.node.handle);
      }

      products.push(product_obj);
    } else {
      console.log('The following product has no variants attached due to wrong attributes', gql_product.node);
    }
  });

  return products;
};

// Products with the most variants and images: https://astr-the-label.myshopify.com/admin/products/4416918650950
// product_ids: Array of product IDs
// return_raw: Boolean. Returns either the prettyfied version or the raw version of the API data
export default async (
  product_ids,
  return_raw = false,
  get_description = false,
  get_metafields = false,
  get_inventory = false,
  get_collections = false,
  get_tags = false,
  get_metafields_for_tiles = false,
  ) => {
  try {
    if (!product_ids.length) {
      return [];
    }

    const query = `
      query getProductsByID @inContext(country: ${window.theme.country_code}, language: ${window.theme.language_code}) {
        products(first: ${product_ids.length}, query: "id:${product_ids.join(' OR id:')}") {
          ${productEdges({get_description, get_collections, get_tags, get_metafields, get_inventory, get_metafields_for_tiles})}
        }
      }
    `;

    // console.log('query', query);

    const response = await sendQuery(query);

    if (!response.ok) {
      // throw new Error(`HTTP error ${response.status}`);
      console.log(`HTTP error ${response.status}`);
      return [];
    }

    const response_json = await response.json();
    // console.log('response_json', response_json);

    if (typeof response_json.errors !== 'undefined') {
      console.log('[Storefront API] Error in response:', response_json.errors);
      return [];
    }

    if (typeof response_json.data === 'undefined') {
      console.log('[Storefront API] No data', response_json);
      return [];
    }

    const original_data = JSON.parse(JSON.stringify(response_json.data.products.edges)); // cloneDeep
    // console.log('Storefront API data (raw)', original_data);

    if (return_raw === true) {
      return original_data;
    }

    // console.log('Load products by ID', response_json);
    const formatted_data = prettyfyProductsByID(response_json.data.products.edges);
    console.log('Storefront API data (prettyfied)', formatted_data);

    return formatted_data;
  } catch (error) {
    console.log('[Storefront API]', error);
    return [];
  }
};
